import { Suspense } from 'react'
import { shallowEqual } from 'react-redux'

import { GrowthBookProvider } from '@growthbook/growthbook-react'
import i18n from 'i18next'
import { polyfill as smoothScrollPolyfill } from 'smoothscroll-polyfill'
import 'swiper/css'
import 'swiper/css/bundle'
import { Spinner } from '~/shared/components/Spinner'
import { PageInfoProvider } from '~/shared/contexts/PageInfoContext'
import { getEnvironment } from '~/shared/helpers/getEnvironment'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import {
  selectConfig,
  selectCurrentVariant,
} from '~/shared/store/common/selects'
import { selectUserStatus } from '~/shared/store/user/selects'
import { appHistory } from '~/shared/utils'

import { useFacebookPixelInitialization } from './hooks/useFacebookPixelInitialization'
import { useGoogleTagManager } from './hooks/useGoogleTagManager'
import { useGrowthBookInitialization } from './hooks/useGrowthBookInitialization'
import { useHotJar } from './hooks/useHotJar'
import { useInContextInitialization } from './hooks/useInContextInitialization'
import { useInitAppConfig } from './hooks/useInitAppConfig'
import { useLanguage } from './hooks/useLanguage'
import { usePinterestPixelInitialization } from './hooks/usePinterestPixelInitialization'
import { useScrollFix } from './hooks/useScrollFix'
import { useSetUserStatus } from './hooks/useSetUserStatus'
import { useSetUuid } from './hooks/useSetUuid'
import { useSetVariant } from './hooks/useSetVariant'
import { AppLayout } from './layouts/AppLayout'
import { growthBookInstance } from './lib/growthBook'
import { I18nInterceptorProvider, i18nInterceptor } from './lib/i18n'
import { setViewportFullHeight } from './lib/setViewportFullHeight'
import { CustomRouter } from './router/CustomRouter'

setViewportFullHeight()
smoothScrollPolyfill()

const useSelectors = () =>
  useBulkSelector({
    userStatus: { select: selectUserStatus, equalityFn: shallowEqual },
    variant: { select: selectCurrentVariant, equalityFn: shallowEqual },
    config: { select: selectConfig, equalityFn: shallowEqual },
  })

export const App = () => {
  const { userStatus, variant, config } = useSelectors()
  const { isStageEnvironment } = getEnvironment()

  if (isStageEnvironment) i18nInterceptor(i18n)

  useInitAppConfig()
  useFacebookPixelInitialization()
  usePinterestPixelInitialization()
  useGoogleTagManager()
  useHotJar()
  useSetVariant()
  useSetUuid()
  useSetUserStatus()
  useGrowthBookInitialization()
  useLanguage()
  useInContextInitialization()
  useScrollFix()

  return (
    <CustomRouter history={appHistory.get()}>
      <Suspense fallback={<Spinner />}>
        <GrowthBookProvider growthbook={growthBookInstance}>
          {userStatus && variant && config ? (
            <PageInfoProvider>
              <I18nInterceptorProvider>
                <AppLayout />
              </I18nInterceptorProvider>
            </PageInfoProvider>
          ) : (
            <Spinner />
          )}
        </GrowthBookProvider>
      </Suspense>
    </CustomRouter>
  )
}
